import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import { config } from "../Const";
import { executeFetchAsync } from "../Util";

const TEMPLATES = [
    ["WRONG_ANSWER", "Wrong answer"],
    ["FULL_PLAYBACK", "Full playback"],
    ["BEAT_THE_CROWD", "Beat the crowd"],
    ["BEAT_THE_CLOCK", "Beat the clock"],
    ["THREE_FACTS", "Three facts"],
];

export default function InstagramReel({ seoTitle, type }) {
    const [template, setTemplate] = useState("WRONG_ANSWER");
    const [caption, setCaption] = useState("");
    const [connection, setConnection] = useState(null);
    const [connectionLoading, setConnectionLoading] = useState(true);
    const [loginPending, setLoginPending] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [publication, setPublication] = useState(null);
    const [message, setMessage] = useState(null);
    const popupTimer = useRef(null);

    const clearPopupTimer = useCallback(() => {
        if (popupTimer.current) {
            window.clearInterval(popupTimer.current);
            popupTimer.current = null;
        }
    }, []);

    useEffect(() => {
        setCaption("");
        setPublication(null);
    }, [seoTitle, template]);

    useEffect(() => () => clearPopupTimer(), [clearPopupTimer]);

    useEffect(() => () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    const loadConnection = useCallback(async () => {
        setConnectionLoading(true);
        try {
            const result = await executeFetchAsync(
                `${config.SERVER_URL}/api/v1/admin/instagram/connection`, "get");
            setConnection(result);
        } catch (error) {
            setConnection(null);
            setMessage({
                severity: "error",
                text: `Could not read Instagram connection: ${error.message}`,
            });
        } finally {
            setConnectionLoading(false);
        }
    }, []);

    useEffect(() => { void loadConnection(); }, [loadConnection]);

    useEffect(() => {
        const receiveOAuth = (event) => {
            if (event.data?.type !== "quizmyday-instagram-oauth") return;
            clearPopupTimer();
            setLoginPending(false);
            if (event.data.success) {
                setMessage({ severity: "success", text: "Instagram connected" });
                void loadConnection();
            } else {
                setMessage({ severity: "error", text: "Instagram authorization failed" });
            }
        };
        window.addEventListener("message", receiveOAuth);
        return () => window.removeEventListener("message", receiveOAuth);
    }, [clearPopupTimer, loadConnection]);

    const connectInstagram = async () => {
        const popup = window.open(
            "", "instagramLogin", "width=620,height=760,resizable=yes,scrollbars=yes");
        if (!popup) {
            setMessage({ severity: "warning", text: "Allow popups to connect Instagram" });
            return;
        }
        clearPopupTimer();
        setLoginPending(true);
        try {
            const response = await executeFetchAsync(
                `${config.SERVER_URL}/api/v1/admin/instagram/authorization-url`, "get");
            popup.location.replace(response.authorizationUrl);
            popupTimer.current = window.setInterval(() => {
                if (popup.closed) {
                    clearPopupTimer();
                    setLoginPending(false);
                    void loadConnection();
                }
            }, 600);
        } catch (error) {
            popup.close();
            setLoginPending(false);
            setMessage({
                severity: "error",
                text: `Could not start Instagram login: ${error.message}`,
            });
        }
    };

    const validationError = useMemo(() => {
        if (type !== "QUIZ") return "Instagram Reels can only be generated for quiz pages.";
        if (caption.length > 2200) return "Caption cannot exceed 2200 characters.";
        return null;
    }, [caption, type]);

    const generatePreview = async () => {
        setGenerating(true);
        try {
            const blob = await executeFetchAsync(
                `${config.SERVER_URL}/api/v1/admin/teaser-video/generate`,
                "post", { "Content-Type": "application/json" },
                JSON.stringify({ quizSeoTitle: seoTitle, template, socialMedia: "INSTAGRAM" }));
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(URL.createObjectURL(blob));

            const query = new URLSearchParams({
                quizSeoTitle: seoTitle,
                template,
                socialMedia: "INSTAGRAM",
            });
            const generatedPublication = await executeFetchAsync(
                `${config.SERVER_URL}/api/v1/admin/teaser-video/publication?${query}`, "get");
            setPublication(generatedPublication);
            if (generatedPublication.description) {
                setCaption(generatedPublication.description);
            }
            setMessage({ severity: "success", text: "Instagram Reel preview generated" });
        } catch (error) {
            setMessage({ severity: "error", text: `Preview generation failed: ${error.message}` });
        } finally {
            setGenerating(false);
        }
    };

    const publishReel = async () => {
        if (validationError) {
            setMessage({ severity: "warning", text: validationError });
            return;
        }
        setPublishing(true);
        try {
            const result = await executeFetchAsync(
                `${config.SERVER_URL}/api/v1/admin/teaser-video/publish`,
                "post", { "Content-Type": "application/json" },
                JSON.stringify({
                    quizSeoTitle: seoTitle,
                    template,
                    socialMedia: "INSTAGRAM",
                    description: caption.trim() || null,
                }));
            setPublication(result);
            setMessage({ severity: "success", text: "Instagram Reel published successfully" });
        } catch (error) {
            setMessage({ severity: "error", text: `Instagram publishing failed: ${error.message}` });
        } finally {
            setPublishing(false);
        }
    };

    if (type !== "QUIZ") {
        return <Alert severity="info">Select a quiz to generate and publish an Instagram Reel.</Alert>;
    }

    const published = publication?.status === "PUBLISHED";

    return (
        <Stack spacing={3} sx={{ width: "100%" }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                       alignItems={{ md: "center" }} justifyContent="space-between">
                    <Box>
                        <Typography variant="subtitle1" fontWeight={700}>Instagram account</Typography>
                        {connectionLoading ? <CircularProgress size={20} /> : connection?.connected ? (
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Chip color="success" size="small" label="Connected" />
                                <Typography variant="body2">@{connection.username}</Typography>
                                {connection.accessTokenExpiresAt && (
                                    <Typography variant="caption" color="text.secondary">
                                        Token expires {new Date(connection.accessTokenExpiresAt).toLocaleString()}
                                    </Typography>
                                )}
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="error">
                                {connection?.lastError || "Instagram is not connected."}
                            </Typography>
                        )}
                    </Box>
                    <Button variant={connection?.connected ? "outlined" : "contained"}
                            sx={{ color: connection?.connected ? "#c13584" : undefined }}
                            startIcon={loginPending
                                ? <CircularProgress size={16} /> : <InstagramIcon />}
                            disabled={loginPending} onClick={connectInstagram}>
                        {connection?.connected ? "Reconnect Instagram" : "Connect Instagram"}
                    </Button>
                </Stack>
            </Paper>

            <Alert severity="info">
                Instagram caption links are plain text. Keep the quiz link in the caption for
                attribution, and use the QuizMyDay profile link as the clickable destination.
            </Alert>

            <Box sx={{ display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) minmax(320px, .8fr)" },
                gap: 3 }}>
                <Stack spacing={2}>
                    <FormControl fullWidth>
                        <InputLabel id="instagram-template-label">Teaser format</InputLabel>
                        <Select labelId="instagram-template-label" value={template}
                                label="Teaser format"
                                onChange={(event) => setTemplate(event.target.value)}>
                            {TEMPLATES.map(([value, label]) => (
                                <MenuItem key={value} value={value}>{label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField fullWidth multiline minRows={7} label="Caption (optional)"
                               value={caption} onChange={(event) => setCaption(event.target.value)}
                               error={caption.length > 2200}
                               helperText={`${caption.length}/2200 characters. Leave blank to use the server-generated caption.`}
                               inputProps={{ maxLength: 2200 }} />

                    {validationError && <Alert severity="warning">{validationError}</Alert>}

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <Button variant="outlined" disabled={generating || publishing}
                                onClick={generatePreview}
                                startIcon={generating ? <CircularProgress size={16} /> : null}>
                            Generate preview
                        </Button>
                        <Button variant="contained" disabled={publishing || generating
                                || !connection?.connected || Boolean(validationError) || published}
                                onClick={publishReel}
                                startIcon={publishing
                                    ? <CircularProgress size={16} /> : <InstagramIcon />}>
                            {published ? "Published to Instagram" : "Publish Instagram Reel"}
                        </Button>
                    </Stack>

                    {publication && (
                        <Alert severity={published ? "success"
                                : publication.status === "FAILED" ? "error" : "info"}>
                            <Typography variant="body2">Status: {publication.status}</Typography>
                            {publication.instagramProcessingStatus && (
                                <Typography variant="caption" display="block">
                                    Instagram: {publication.instagramProcessingStatus}
                                </Typography>
                            )}
                            {publication.failureMessage && (
                                <Typography variant="caption" display="block">
                                    {publication.failureMessage}
                                </Typography>
                            )}
                            {publication.externalPublicationUrl && (
                                <Link href={publication.externalPublicationUrl} target="_blank"
                                      rel="noreferrer" display="inline-block" sx={{ mt: 1 }}>
                                    Open the published Reel
                                </Link>
                            )}
                        </Alert>
                    )}
                </Stack>

                <Paper variant="outlined" sx={{ p: 2, minHeight: 320 }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                        Video preview
                    </Typography>
                    {previewUrl ? (
                        <Box component="video" src={previewUrl} controls sx={{
                            width: "100%", maxHeight: 620, bgcolor: "black", borderRadius: 1,
                        }} />
                    ) : (
                        <Box sx={{ minHeight: 260, display: "grid", placeItems: "center",
                            bgcolor: "grey.100", borderRadius: 1, p: 3 }}>
                            <Typography color="text.secondary" textAlign="center">
                                Generate a preview to inspect the exact vertical Reel before publishing.
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Box>

            <Snackbar open={Boolean(message)} autoHideDuration={6000}
                      onClose={() => setMessage(null)}>
                {message ? (
                    <Alert severity={message.severity} onClose={() => setMessage(null)}>
                        {message.text}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </Stack>
    );
}
